import { groupBy, isEqual, uniqWith } from 'lodash';
import {
  IDictionaryIndustry,
  IDictionarySector,
} from '@/finsight/application/Dictionaries/helpers';
import {
  ARTICLE_GROUP_ID,
  ARTICLE_SUBGROUP_ID,
  ARTICLE_SUBSCRIPTION_ID,
} from '@/finsight/ui/components/SubscriptionForm/const';
import { IUserSubscriptionsResponse } from '@/finsight/domain/vo/Subscriptions/userSubscriptions';
import { mapArticleTypeToTitle } from '@/finsight/ui/components/creditFlow/articles/constants';
import { ThemeType } from '@/finsight/domain/vo/CreditFlow/Articles/themeTypes';
import { ProductAbbreviations } from '@/finsight/domain/Product';
import { ProfileTypes } from '@/finsight/application/actions/profile/constants';
import URL from '@/finsight/Router/urlConstants';
import { ISSUER } from '@/finsight/application/actions/pageFilters/constants';
import { IMarketTheme } from '@/finsight/domain/vo/CreditFlow/IMarketTheme';
import { ISubscriptionsPayloadTheme } from '@/finsight/domain/vo/Subscriptions';
import { IRegion } from '@/finsight/domain/vo/Region';
import { IProduct } from '@/finsight/domain/vo/Product';

export enum PageTypes {
  SECTOR = 'sectors',
  INDUSTRY = 'industries',
  COMPANY = 'companies',
  OVERVIEW = 'overview',
}

const getAllSubById = <T>(
  list: T[],
  id: string,
  property: string,
  subProperty: string,
): T[] => list.filter((entity) => entity[property] === id && entity[subProperty]);

type SubEntitiesFilter = {
  [x: string]: string,
  regionId: string,
}[];

export const getSubEntitiesFilter = (
  parent: IDictionarySector | IDictionaryIndustry,
  list: (IDictionarySector | IDictionaryIndustry)[],
  regionId: string,
  parentProperty: string,
  subProperty: string,
  payloadProperty: string,
): SubEntitiesFilter => {
  if (parent?.[subProperty]) {
    return [{ [payloadProperty]: parent[subProperty], regionId }];
  }

  if (parent?.[parentProperty]) {
    return getAllSubById<IDictionarySector | IDictionaryIndustry>(
      list, parent[parentProperty],
      parentProperty,
      subProperty,
    ).map((entity) => ({ [payloadProperty]: entity[subProperty], regionId }));
  }

  return [];
};

const getThemesConsumerOverview = (themes: IMarketTheme[], themeType: ThemeType, regionSet: Set<string>) => {
  const groupedThemesByType = groupBy(themes, 'type');
  const themesByType = groupedThemesByType[mapArticleTypeToTitle[themeType]] || [];
  return themesByType.filter(
      ({ subtype, regionId }) => subtype === ProductAbbreviations.ABS && regionSet.has(regionId),
  );
};

export const getThemesByRegionsProducts = ({
  themes,
  regionIds,
  productIds,
  isConsumerOverview,
  themeType,
}: {
  themes: IMarketTheme[],
  regionIds: string[],
  productIds: string[],
  isConsumerOverview: boolean,
  themeType: ThemeType,
}) => {
  const regionSet = new Set(regionIds);

  if (isConsumerOverview && !!themeType) {
    return getThemesConsumerOverview(themes, themeType, regionSet);
  }

  const productSet = new Set(productIds);
  return themes.filter(({ productId, regionId, type }) => (
      regionSet.has(regionId) && productSet.has(productId)) && type === mapArticleTypeToTitle[themeType],
  );
};

export const initialArticleParams = {
  countryIds: [],
  sectors: [],
  companies: [],
  industries: [],
  themes: [],
};

export const getSubscriptionsArticleParams = (mySubscriptions: IUserSubscriptionsResponse) => {
  const articleParams = mySubscriptions[ARTICLE_GROUP_ID]
      ?.subgroups[ARTICLE_SUBGROUP_ID]
      ?.subscriptions[ARTICLE_SUBSCRIPTION_ID]
      ?.articleParams || initialArticleParams;

  const subscriptionIds: string[] = [];

  Object.values(mySubscriptions).forEach(({ subgroups }) => {
    if (!subgroups) return;

    Object.values(subgroups).forEach(({ subscriptions }) => {
      if (!subscriptions) return;

      Object.values(subscriptions).forEach((sub) => {
        if (sub.isSubscribed) {
          subscriptionIds.push(sub.subscriptionId);
        }
      });
    });
  });

  return { articleParams, subscriptionIds };
};

const profileParamsMap: Record<ProfileTypes, ParamsGenerator> = {
  [ProfileTypes.INDUSTRY]: ({ industry, regionIds, industriesList }) => ({
    industries: getSubEntitiesFilter(
      industry,
      Object.values(industriesList),
      regionIds[0],
      'industryId',
      'subindustryId',
      'subIndustryId',
    ),
  }),
  [ProfileTypes.SECTOR]: ({ sector, regionIds, sectorsList }) => ({
    sectors: getSubEntitiesFilter(
      sector,
      Object.values(sectorsList),
      regionIds[0],
      'sectorId',
      'subsectorId',
      'subSectorId',
    ),
  }),
  [ProfileTypes.OVERVIEW]: ({ themes, regionIds, productIds, isConsumerOverview, themeType }) => ({
    themes: getThemesByRegionsProducts({
      themes,
      regionIds,
      productIds,
      isConsumerOverview,
      themeType,
    }).map(({ id, productId, regionId }) => ({
      themeId: id,
      productId,
      regionId,
    })),
  }),
};

export interface IArticleParamsConfig {
  themes: IMarketTheme[],
  regionIds: string[],
  productIds: string[],
  isConsumerOverview: boolean,
  themeType: ThemeType,
  sector: IDictionarySector,
  industry: IDictionaryIndustry,
  sectorsList: Record<string, IDictionarySector>,
  industriesList: Record<string, IDictionaryIndustry>,
  includedParentId: string,
  includedCompanyRole: string,
  pathname: string,
  profileType: ProfileTypes,
}

type ParamsGenerator = (
  config: IArticleParamsConfig
) => Record<string, SubEntitiesFilter | ISubscriptionsPayloadTheme[]>;

const getCompanyFilter = ({ includedParentId, includedCompanyRole, pathname }) => {
  if (includedCompanyRole && includedParentId) {
    return [{
      companyId: includedParentId,
      companyType: pathname.includes(URL.ISSUER) ? ISSUER : includedCompanyRole,
    }];
  }
  return [];
};

export const getParams = (config: IArticleParamsConfig) => {
  const paramsGenerator = profileParamsMap[config.profileType];

  return {
    ...initialArticleParams,
    ...(paramsGenerator && paramsGenerator(config)),
    companies: getCompanyFilter(config),
  };
};

export const getPageIsOpened = (query) => {
  const { companyViewId, sector, industry } = query;

  if (companyViewId) return PageTypes.COMPANY;
  if (sector) return PageTypes.SECTOR;
  if (industry) return PageTypes.INDUSTRY;

  return PageTypes.OVERVIEW;
};

const mergeUniqueArrays = (
    arr1: any[],
    arr2: any[] = [],
    arr3: any[] = [],
) => uniqWith([...arr1, ...arr2, ...arr3], isEqual);

export const getSubscribeParams = ({
  subscriptionArticleParams = initialArticleParams,
  paramsConfig,
  pageType,
  articleFormThemes = null,
}) => {
  const {
    sectors,
    themes: paramsThemes,
    industries,
    companies,
  } = getParams(paramsConfig);
  const articleParams = { ...subscriptionArticleParams };

  const pageTypeParamsMap = {
    [PageTypes.SECTOR]: sectors,
    [PageTypes.INDUSTRY]: industries,
    [PageTypes.COMPANY]: companies,
  };

  if (pageType && articleParams[pageType] && pageTypeParamsMap[pageType]) {
    articleParams[pageType] = mergeUniqueArrays(subscriptionArticleParams[pageType], pageTypeParamsMap[pageType]);
    return articleParams;
  }

  articleParams.themes = Array.isArray(articleFormThemes)
      ? articleFormThemes
      : mergeUniqueArrays(subscriptionArticleParams.themes, paramsThemes);

  return articleParams;
};

export const getFilteredThemes = (
  themes: IMarketTheme[],
  currentRegion: IRegion,
  currentProduct: IProduct,
) => themes.filter(
  ({ regionId, productId }) => regionId === currentRegion.id && productId === currentProduct.id,
);
